import { h } from '@/components/dom';
import { button, iconButton, segmented, setButtonContent } from '@/components/controls';
import { icons } from '@/components/icons';
import type { App } from './App';
import { sequenceView } from '@/state/appState';

const SPEEDS = [10, 50, 100, 400, 1000, 4000];

export function createTimeline(app: App): HTMLElement {
  const play = button({ label: 'Play', icon: icons.play, title: 'Play / pause the construction (Space)', onClick: () => app.playback.toggle() });
  play.classList.add('timeline__play');
  const restart = iconButton({ icon: icons.skipStart, title: 'Restart (Home)', onClick: () => app.playback.restart() });
  const back = iconButton({ icon: icons.stepBack, title: 'Step back (←)', onClick: () => app.playback.step(-1) });
  const forward = iconButton({ icon: icons.stepForward, title: 'Step forward (→)', onClick: () => app.playback.step(1) });
  const end = iconButton({ icon: icons.skipEnd, title: 'Jump to the end (End)', onClick: () => app.playback.toEnd() });
  const speed = segmented<string>({
    label: 'Playback speed',
    size: 'sm',
    value: String(app.state.playback.speed),
    options: SPEEDS.map((v) => ({ value: String(v), label: v >= 1000 ? `${v / 1000}k` : String(v), title: `${v} strings per second` })),
    onChange: (v) => app.playback.setSpeed(Number(v)),
  });
  const scrub = h('input', { type: 'range', min: '0', max: '0', step: '1', value: '0', class: 'timeline-scrub', attrs: { 'aria-label': 'Timeline' } });
  scrub.addEventListener('input', () => app.playback.seek(Number(scrub.value)));
  const readout = h('div', { class: 'timeline__readout' });
  const fill = h('i', { class: 'timeline__fill' });
  const bar = h('div', { class: 'timeline__bar', attrs: { 'aria-hidden': 'true' } }, fill);
  const element = h(
    'footer',
    { class: 'timeline' },
    bar,
    h('div', { class: 'timeline__transport' }, restart, back, play, forward, end),
    scrub,
    readout,
    h('div', { class: 'timeline__speed' }, h('span', null, 'Speed'), speed.element),
  );

  app.store.subscribe((s, p) => {
    if (s.playback !== p.playback || s.solution !== p.solution || s.status !== p.status || s === p) {
      const total = s.solution.count;
      scrub.max = String(total);
      if (document.activeElement !== scrub) scrub.value = String(Math.min(s.playback.index, total));
      setButtonContent(play, s.playback.playing ? 'Pause' : 'Play', s.playback.playing ? icons.pause : icons.play);
      play.disabled = total === 0;
      for (const b of [restart, back, forward, end]) b.disabled = total === 0;
      speed.setValue(String(s.playback.speed));
      const i = Math.min(s.playback.index, total);
      const seq = sequenceView(s.solution);
      const cur = i > 0 ? i - 1 : -1;
      const parts = [`String ${i} / ${total}`];
      if (cur >= 0) {
        parts.push(`pin ${seq[cur * 2]} → ${seq[cur * 2 + 1]}`);
        if (cur < s.solution.errors.length) parts.push(`error ${s.solution.errors[cur].toFixed(4)}`);
        if (cur < s.solution.scores.length) parts.push(`improvement ${s.solution.scores[cur].toFixed(5)}`);
      } else if (s.solution.initialError > 0) {
        parts.push(`error ${s.solution.initialError.toFixed(4)}`);
      }
      readout.textContent = parts.join(', ');
      const max = s.config.algorithm.maxStrings;
      fill.style.width = `${Math.min(100, (total / Math.max(1, max)) * 100).toFixed(2)}%`;
      element.classList.toggle('is-running', s.status === 'running');
      bar.title = `${total} / ${max} strings`;
    }
  }, { immediate: true });

  return element;
}
